import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { EbookPageContext } from "../../store/ebookPageContext";
import { LanguageContext } from "../LanguageContext/LanguageContext";
import Popup from "../Popup/Popup";
import auth from "../../Auth/Auth";

// Import both styles
import enStyles from "./EbooksCardsEn.module.css";
import arStyles from "./EbooksCardsAr.module.css";

const EbooksCards = () => {
  const {
    ebookPageContextDetails,
    setEbookPageContextDetails,
    loading,
    error,
  } = useContext(EbookPageContext);
  const { language } = useContext(LanguageContext); // Get language context
  const [displayCount, setDisplayCount] = useState(8);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedEbook, setSelectedEbook] = useState(null);
  const navigate = useNavigate();

  const userDetails = auth.getAuthData();

  // Choose styles based on the current language
  const styles = language === "ar" ? arStyles : enStyles;

  useEffect(() => {
    const fetchEbooks = async () => {
      try {
        const response = await fetch("/api/ebookData");
        const data = await response.json();
        setEbookPageContextDetails(data);
      } catch (err) {
        console.error("Error fetching data", err);
      }
    };
    fetchEbooks();
  }, [setEbookPageContextDetails]);

  const handleDownloadClick = (ebookUrl) => {
    window.location.href = ebookUrl;
  };

  const openPopup = (ebook) => {
    const courseCount = localStorage.getItem("courseCount");
    if (!userDetails || !userDetails.token || courseCount == 0) {
      setSelectedEbook(ebook);
      setIsPopupOpen(true);
    } else {
      handleDownloadClick(ebook.ebookPdfUrl);
    }
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedEbook(null);
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading data</p>;

  const { EbookDataTitle, ebook } = ebookPageContextDetails || {};
  const { title } = EbookDataTitle || { title: "Default Title" };

  const loadMoreItems = () => {
    setDisplayCount((prevCount) => prevCount + 8);
  };

  return (
    <div
      className={`${styles.container} ${isPopupOpen ? styles.fixed : ""}`}
      dir={language === "ar" ? "rtl" : "ltr"} // Apply direction dynamically
    >
      <div>
        <h1 className={styles.containerHeader}>{title}</h1>
      </div>

      <div className={styles.cardsContainer}>
        {ebook?.slice(0, displayCount).map((ebookItem) => (
          <div key={ebookItem.id} className={styles.card}>
            <img
              src={ebookItem.imgSrc}
              alt={ebookItem.title}
              className={styles.image}
            />
            <div className={styles.content}>
              <p>{ebookItem.text}</p>
              <h3 className={styles.text}>{ebookItem.title}</h3>
              <div className={styles.btn}>
                {ebookItem.comingSoon ? (
                  <button className={styles.soonButton1}>Soon</button>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: "8px",
                      width: "100%",
                    }}
                  >
                    {/* Download Button with integrated icon */}
                    <div
                      onClick={() => openPopup(ebookItem)}
                      className="download-button"
                      style={{
                        backgroundColor: "#39FFFB",
                        padding: "10px 24px",
                        borderRadius: "64px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "12px",
                        color: "#061C2B",
                        fontSize: "17px",
                      }}
                    >
                      <span style={{ fontWeight: "800" }}>{ebookItem.buttonText.text}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        viewBox="0 0 42 42"
                        fill="none"
                        style={{ transform: 'scale(1.1)' }}
                      >
                        <path
                          d="M29.2514 23.0107C28.8282 23.0107 28.5027 23.3362 28.5027 23.7594V26.754C28.5027 27.1771 28.1772 27.5026 27.7541 27.5026H14.2459C13.8228 27.5026 13.4973 27.1771 13.4973 26.754V23.7594C13.4973 23.3362 13.1718 23.0107 12.7486 23.0107C12.3255 23.0107 12 23.3362 12 23.7594V26.754C12 27.9909 13.009 28.9999 14.2459 28.9999H27.7541C28.991 28.9999 30 27.9909 30 26.754V23.7594C30 23.3688 29.6745 23.0107 29.2514 23.0107Z"
                          fill="#061C2B"
                          stroke="#061C2B"
                          strokeWidth="0.8"
                        />
                        <path
                          d="M15.9726 21.1881L19.4229 24.6383C20.3018 25.5172 21.7339 25.5172 22.6128 24.6383L26.0631 21.1881C26.356 20.8951 26.3886 20.4394 26.0956 20.1139C25.8027 19.7884 25.347 19.7884 25.0215 20.0814L24.9889 20.1139L21.7665 23.3363V11.7486C21.7665 11.3255 21.441 11 21.0178 11C20.5947 11 20.2692 11.3255 20.2692 11.7486V23.3363L17.0468 20.1139C16.7538 19.821 16.2981 19.7884 15.9726 20.0814C15.6471 20.3743 15.6471 20.8626 15.9726 21.1881C15.9401 21.1555 15.9401 21.1555 15.9726 21.1881Z"
                          fill="#061C2B"
                          stroke="#061C2B"
                          strokeWidth="0.8"
                        />
                      </svg>
                    </div>

                    {/* Heart/Favorite Icon */}
                    <div
                      style={{
                        width: "42px",
                        height: "42px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 42 42"
                        width="100%"
                        height="100%"
                        fill="none"
                      >
                        <rect
                          width="42"
                          height="42"
                          rx="21"
                          fill="white"
                          fillOpacity="0.2"
                        />
                        <path
                          d="M29.3666 14.0333C28.7499 13.4167 27.9832 12.9583 27.1399 12.6917C26.2966 12.425 25.4033 12.3583 24.5299 12.4917C23.6566 12.625 22.8299 12.9417 22.1166 13.4167C21.7499 13.6667 21.4166 13.9583 21.1166 14.2917C20.8166 13.9583 20.4833 13.6667 20.1166 13.4167C19.4033 12.9417 18.5766 12.625 17.7033 12.4917C16.8299 12.3583 15.9366 12.425 15.0933 12.6917C14.2499 12.9583 13.4833 13.4167 12.8666 14.0333C11.0499 15.85 10.4666 18.3583 11.2333 20.6417C11.9999 22.925 13.9499 24.6417 16.3333 25.1417L21.1166 29.925L25.8999 25.1417C28.2833 24.6417 30.2333 22.925 30.9999 20.6417C31.7666 18.3583 31.1833 15.85 29.3666 14.0333Z"
                          fill="#39FFFB"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {ebook && displayCount < ebook.length && (
        <button onClick={loadMoreItems} className={styles.loadMoreButton}>
          Load More ({displayCount}/{ebook.length})
        </button>
      )}

      <Popup
        isOpen={isPopupOpen}
        onClose={closePopup}
        ebookData={selectedEbook}
        onDownload={() => handleDownloadClick(selectedEbook?.ebookPdfUrl)}
      />
    </div>
  );
};

export default EbooksCards;
